/**
 * Local-dev token provider: the bearer string IS the Firebase uid — the
 * `ADAPTERS=local` API's LocalAuthAdapter verifies by echoing it back, so a
 * seeded uid (`seed-firebase-free` / `seed-firebase-paid`) authenticates as
 * that user with no Firebase anywhere. Refused in production by the API-side
 * bootstrap guard; the web build guards via NEXT_PUBLIC_AUTH_MODE semantics.
 */
import {
  ANONYMOUS_SNAPSHOT,
  LOADING_SNAPSHOT,
  type AuthSnapshot,
  type TokenProvider,
} from "./token-provider";

/** localStorage key holding the dev bearer (a seeded firebaseUid). */
export const DEV_BEARER_STORAGE_KEY = "reflect.dev.bearer";

/** Local-dev {@link TokenProvider}; also exposes `setToken` for the dev form. */
export interface LocalTokenProvider extends TokenProvider {
  /** Store a dev bearer and flip to authenticated. */
  setToken(uid: string): void;
}

/**
 * Build the local provider. Storage is read lazily on the client only —
 * constructing during prerender must not touch `window`.
 */
export function createLocalTokenProvider(): LocalTokenProvider {
  let snapshot: AuthSnapshot = LOADING_SNAPSHOT;
  let initialized = false;
  // In-memory token — the tab's source of truth. localStorage is only the
  // persistence layer (survive reloads): if a storage write fails, the
  // published snapshot and the bearer STILL agree for this tab's lifetime.
  // Reading storage per request instead would let a failed write desync them —
  // authenticated UI with an anonymous (or stale-identity) bearer.
  let token: string | null = null;
  const listeners = new Set<() => void>();

  function publish(next: AuthSnapshot): void {
    snapshot = next;
    for (const listener of listeners) listener();
  }

  function readStoredToken(): string | null {
    try {
      return window.localStorage.getItem(DEV_BEARER_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  function ensureInitialized(): void {
    if (initialized || typeof window === "undefined") return;
    initialized = true;
    token = readStoredToken();
    publish(token ? { status: "authenticated", providerUid: token } : ANONYMOUS_SNAPSHOT);
  }

  return {
    getSnapshot() {
      ensureInitialized();
      return snapshot;
    },
    subscribe(listener) {
      ensureInitialized();
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getIdToken() {
      ensureInitialized();
      return Promise.resolve(token);
    },
    setToken(uid) {
      token = uid;
      try {
        window.localStorage.setItem(DEV_BEARER_STORAGE_KEY, uid);
      } catch {
        // Persistence lost (blocked storage/quota) — the in-memory token
        // keeps this tab's session coherent; it just won't survive a reload.
      }
      publish({ status: "authenticated", providerUid: uid });
    },
    signOut() {
      token = null;
      try {
        window.localStorage.removeItem(DEV_BEARER_STORAGE_KEY);
      } catch {
        // Storage unavailable — the cleared in-memory token still signs the
        // tab out; a stale persisted value only resurfaces on a full reload.
      }
      publish(ANONYMOUS_SNAPSHOT);
      return Promise.resolve();
    },
  };
}
